import React, { Fragment, useEffect, useMemo, useState } from "react";
import Accordion from "../../../components/Accordion/Accordion";
import "../../../styles/components/devicesettings.scss";
import { useDevice, useDeviceGroup, useModels } from "../../../Api/newSockets.api";
import dataApi from "../../../Api/data.api";
import DeviceMenu from "./DeviceMenu";
import DevicesHeader from "../../../components/Header/DevicesHeader";
import LoadingSpinner from "../../../components/LoadingSpinner/LoadingSpinner";
import Tooltip from "../../../components/Tooltip/Tooltip";
import { useLocation } from "react-router-dom";
import { savePathToLocalStorage } from "../../../utils/helpers";
import { DeviceTypeId } from "../../../constants/deviceTypes";
import Select from "../../../components/DataEntry/Select";
import Modal from "../../../components/Modals/Modal";
import Input from "../../../components/DataEntry/Input";

type Props = {
  label: string;
  icon: any;
  children?: any;
  data?: any;
  match?: any;
};

const Settings = (props: Props) => {
  savePathToLocalStorage();

  const deviceId = props.match.params.deviceId;
  // const groupId = props.data?.groupId;
  const { getDevice, setDeviceSettings } = useDevice();
  const { getDeviceGroup } = useDeviceGroup();
  const { models, searchModels, connected: modelsConnected } = useModels();

  // Datasets are fetched over REST (request/response) rather than via the socket
  // 'datasets' event, which is race-prone on a hard reload: the emit and the event
  // listener attach in separate effects, so a fast response can arrive before the
  // listener exists and is silently dropped, leaving this dropdown empty. A REST GET
  // on mount has no such race and is retryable.
  const [datasets, setDatasets] = useState<any[]>([]);

  const fetchDatasets = async () => {
    try {
      const res = await dataApi.getDatasets(0, 200, { type: 'Metrics' });
      setDatasets(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      console.error('Failed to fetch datasets:', err);
    }
  };
  const { device } = getDevice(parseInt(deviceId));
  // const { deviceGroup } = getDeviceGroup(parseInt());
  const data = device;
  const groupId = device?.groupId;
  const groupIdItem = getDeviceGroup(parseInt(groupId));
  console.log(groupIdItem);

  // Create dataset modal state
  const [createDatasetModalOpen, setCreateDatasetModalOpen] = useState(false);
  const [newDatasetName, setNewDatasetName] = useState("");
  const [creatingDataset, setCreatingDataset] = useState(false);

  const settings = data?.settings ?? {};
  const calibration = settings?.calibration ?? settings;

  const selectedModelId = settings?.modelId != null ? String(settings.modelId) : "";
  const selectedDefaultDatasetId =
    settings?.defaultDataset != null ? String(settings.defaultDataset) : "";

  const modelOptions = useMemo(() => {
    const arr = Array.isArray(models) ? models : [];
    return arr
      .filter((m: any) => m && m.id != null)
      .map((m: any) => ({ id: String(m.id), name: m.name ?? String(m.id) }));
  }, [models]);

  const datasetOptions = useMemo(() => {
    const arr = Array.isArray(datasets) ? datasets : [];
    return arr
      .filter((d: any) => d && d.id != null)
      .map((d: any) => ({ id: String(d.id), name: d.name ?? String(d.id) }));
  }, [datasets]);

  useEffect(() => {
    if (modelsConnected) searchModels("").catch(() => {});
  }, [modelsConnected]);

  useEffect(() => {
    /* Metrics default dataset: only "Metrics" datasets receive device_metrics rows in the UI.
       Fetched once on mount via REST (no socket-event timing race). */
    fetchDatasets();
  }, []);

  const updateSetting = (patch: any) => {
    setDeviceSettings(parseInt(deviceId), patch);
  };

  const handleCreateDataset = async () => {
    if (!newDatasetName.trim()) return;
    setCreatingDataset(true);
    try {
      const created: any = await dataApi.createDataset(
        newDatasetName.trim(),
        'Metrics',
        `Metrics dataset for ${data?.name || 'device'}`
      );
      // Auto-select the newly created dataset
      if (created?.id) {
        updateSetting({ defaultDataset: String(created.id) });
      }
      // Refresh datasets list
      await fetchDatasets();
      setCreateDatasetModalOpen(false);
      setNewDatasetName("");
    } catch (err) {
      console.error("Failed to create dataset:", err);
    } finally {
      setCreatingDataset(false);
    }
  };

  const openCreateDatasetModal = () => {
    // Pre-fill with device name
    setNewDatasetName(data?.name ? `${data.name} Metrics` : "");
    setCreateDatasetModalOpen(true);
  };

  return data ? (
    <div className="device-main main-content">
      <DevicesHeader device={device} />
      <div className="general-body-content">
        <div>
          <DeviceMenu typeId={device.deviceTypes?.id} />
        </div>
        <div className="general">
          <Accordion title="General" expanded>
            <div className="settings-header-basic-info settings-header">
              Basic info
            </div>
            <div className="settings-general-details">
              <div className="settings-general-details-row">
                <div className="settings-column">
                  <div className="settings-label">Board:</div>
                  <div className="settings-data">{data.board}</div>
                </div>
                {data.connectedTo && (
                  <div className="settings-column">
                    <div className="settings-label">Connected to:</div>
                    <div className="settings-data">{data.connectedTo}</div>
                  </div>
                )}
                {data.groupId && groupIdItem?.deviceGroup && (
                  <div className="settings-column">
                    <div className="settings-label">Group name:</div>
                    <div className="settings-data">
                      {groupIdItem.deviceGroup.name}
                    </div>
                  </div>
                )}
                {data.id && (
                  <div className="settings-column">
                    <div className="settings-label">ID:</div>
                    <div className="settings-data">{data.id}</div>
                  </div>
                )}
                {data.name && (
                  <div className="settings-column">
                    <div className="settings-label">Name:</div>
                    <div className="settings-data">{data.name}</div>
                  </div>
                )}
                {data.port && (
                  <div className="settings-column">
                    <div className="settings-label">Port:</div>
                    <div className="settings-data">{data.port}</div>
                  </div>
                )}
                {data.programId && (
                  <div className="settings-column">
                    <div className="settings-label">Program Id:</div>
                    <div className="settings-data">{data.programId}</div>
                  </div>
                )}
                <div className="settings-column">
                  <div className="settings-label">Status:</div>
                  <div className="settings-data">{data.status}</div>
                </div>
              </div>
            </div>
            {/* Device Type Object */}
            <div className="settings-header-device-type settings-header">
              Device type info
            </div>
            <div className="settings-general-details">
              <div className="settings-general-details-row">
                {data.deviceTypes?.name && (
                  <div className="settings-column">
                    <div className="settings-label">Name:</div>
                    <div className="settings-data">{data.deviceTypes?.name}</div>
                  </div>
                )}
                {data.deviceTypes?.description && (
                  <div className="settings-column">
                    <div className="settings-label">Description:</div>
                    <div className="settings-data">
                      {data.deviceTypes?.description}
                    </div>
                  </div>
                )}
              </div>
            </div>
            {data.deviceTypes?.id === DeviceTypeId.HOST && (
              <Fragment>
                <div className="settings-header-settings settings-header">
                  Settings info
                </div>
                <div className="settings-general-details">
                  <div className="settings-general-details-row settings-row-grid">
                    <div className="settings-column">
                      <div className="settings-label">Default dataset:</div>
                      <div className="settings-data" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Select
                          placeholder="Select dataset"
                          options={datasetOptions}
                          value={selectedDefaultDatasetId}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                            updateSetting({ defaultDataset: e.target.value })
                          }
                          className="select-classic"
                        />
                        <button
                          onClick={openCreateDatasetModal}
                          style={{
                            padding: '6px 12px',
                            background: '#2196f3',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          + New
                        </button>
                      </div>
                    </div>
                    {settings.cameraStreamEndpoint && (
                      <div className="settings-column">
                        <div className="settings-label">
                          Camera Stream Endpoint:
                        </div>
                        <div className="settings-data">
                          {settings.cameraStreamEndpoint}
                        </div>
                      </div>
                    )}
                    {settings.sshEndpoint && (
                      <div className="settings-column">
                        <div className="settings-label">SSH Endpoint:</div>
                        <div className="settings-data">
                          {settings.sshEndpoint}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Fragment>
            )}
            {data.deviceTypes?.id === DeviceTypeId.CAMERA && (
              <Fragment>
                <div className="settings-header-settings settings-header">
                  Settings info
                </div>
                <div className="settings-general-details">
                  <div className="settings-general-details-row settings-row-grid">
                    {settings.cameraStreamEndpoint && (
                      <div className="settings-column">
                        <div className="settings-label">Stream endpoint:</div>
                        <div className="settings-data">
                          {settings.cameraStreamEndpoint}
                        </div>
                      </div>
                    )}

                    <div className="settings-column">
                      <div className="settings-label">Model:</div>
                      <div className="settings-data">
                        <Select
                          placeholder="Select model"
                          options={modelOptions}
                          value={selectedModelId}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                            updateSetting({ modelId: e.target.value })
                          }
                          className="select-classic"
                        />
                      </div>
                    </div>

                    <div className="settings-column">
                      <div className="settings-label">Default dataset:</div>
                      <div className="settings-data" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Select
                          placeholder="Select dataset"
                          options={datasetOptions}
                          value={selectedDefaultDatasetId}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                            updateSetting({ defaultDataset: e.target.value })
                          }
                          className="select-classic"
                        />
                        <button
                          onClick={openCreateDatasetModal}
                          style={{
                            padding: '6px 12px',
                            background: '#2196f3',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          + New
                        </button>
                      </div>
                    </div>

                    {settings.position && (
                      <div className="settings-column">
                        <div className="settings-label">Position: </div>
                        <div className="settings-data">
                          {settings.position.map((position) => (
                            <>
                              <div>
                                <span>x0:</span>
                                {position.x0}
                              </div>
                              <div>
                                <span>x1:</span>
                                {position.x1}
                              </div>
                              <div>
                                <span>y0:</span>
                                {position.y0}
                              </div>
                              <div>
                                <span>y1:</span>
                                {position.y1}
                              </div>
                            </>
                          ))}
                        </div>
                      </div>
                    )}

                    {calibration?.points && (
                      <div className="settings-column">
                        <div className="settings-label">Calibration:</div>
                        <div className="settings-data">
                          {calibration.rows} x {calibration.cols} | {calibration.square_mm}mm
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Fragment>
            )}
            {(data.deviceTypes?.id === DeviceTypeId.PLC ||
              data.deviceTypes?.id === DeviceTypeId.USB) && (
              <Fragment>
                <div className="settings-header-settings settings-header">
                  Settings info
                </div>
                <div className="settings-general-details">
                  <div className="settings-general-details-row settings-row-grid">
                    <div className="settings-column">
                      <div className="settings-label">Default dataset:</div>
                      <div className="settings-data" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <Select
                          placeholder="Select dataset"
                          options={datasetOptions}
                          value={selectedDefaultDatasetId}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                            updateSetting({ defaultDataset: e.target.value })
                          }
                          className="select-classic"
                        />
                        <button
                          onClick={openCreateDatasetModal}
                          style={{
                            padding: '6px 12px',
                            background: '#2196f3',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          + New
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </Fragment>
            )}
            {data.deviceTypes?.id === DeviceTypeId.ROBOT_ARM && (
              <Fragment>
                <div className="settings-column">
                  <div className="settings-label"></div>
                  <div className="settings-data"></div>
                </div>
              </Fragment>
            )}
          </Accordion>
          <Tooltip content="Not implemented">
            <Accordion title="System"></Accordion>
          </Tooltip>
          <Tooltip content="Not implemented">
            <Accordion title="Smart alerts"></Accordion>
          </Tooltip>
        </div>
      </div>

      {/* Create Dataset Modal */}
      <Modal
        visible={createDatasetModalOpen}
        onClose={() => setCreateDatasetModalOpen(false)}
        onSubmit={handleCreateDataset}
        title="Create Metrics Dataset"
        subtitle="Create a new dataset to store device metrics"
        submitLoading={creatingDataset}
      >
        <div style={{ marginTop: '16px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
            Dataset Name
          </label>
          <Input
            placeholder="Enter dataset name"
            value={newDatasetName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewDatasetName(e.target.value)}
          />
        </div>
      </Modal>
    </div>
  ) : (
    <LoadingSpinner />
  );
};

export default Settings;
